"use client";
import { useState, useEffect, useRef } from "react";
import { ArrowRight, Link as LinkIcon, Zap } from "lucide-react";

import { cn } from "../../lib/utils";

// Rebranded (light, Furloop palette) radial orbital timeline.
export default function RadialOrbitalTimeline({ timelineData }) {
  const [expandedItems, setExpandedItems] = useState({});
  const [rotationAngle, setRotationAngle] = useState(0);
  const [autoRotate, setAutoRotate] = useState(true);
  const [pulseEffect, setPulseEffect] = useState({});
  const [activeNodeId, setActiveNodeId] = useState(null);
  const containerRef = useRef(null);
  const orbitRef = useRef(null);
  const nodeRefs = useRef({});

  const handleContainerClick = (e) => {
    if (e.target === containerRef.current || e.target === orbitRef.current) {
      setExpandedItems({});
      setActiveNodeId(null);
      setPulseEffect({});
      setAutoRotate(true);
    }
  };

  const getRelatedItems = (itemId) => {
    const currentItem = timelineData.find((item) => item.id === itemId);
    return currentItem ? currentItem.relatedIds : [];
  };

  const centerViewOnNode = (nodeId) => {
    if (!nodeRefs.current[nodeId]) return;
    const nodeIndex = timelineData.findIndex((item) => item.id === nodeId);
    const targetAngle = (nodeIndex / timelineData.length) * 360;
    setRotationAngle(270 - targetAngle);
  };

  const toggleItem = (id) => {
    setExpandedItems((prev) => {
      const newState = { ...prev };
      Object.keys(newState).forEach((key) => {
        if (parseInt(key, 10) !== id) newState[parseInt(key, 10)] = false;
      });
      newState[id] = !prev[id];

      if (!prev[id]) {
        setActiveNodeId(id);
        setAutoRotate(false);
        const newPulse = {};
        getRelatedItems(id).forEach((relId) => {
          newPulse[relId] = true;
        });
        setPulseEffect(newPulse);
        centerViewOnNode(id);
      } else {
        setActiveNodeId(null);
        setAutoRotate(true);
        setPulseEffect({});
      }
      return newState;
    });
  };

  useEffect(() => {
    let rotationTimer;
    if (autoRotate) {
      rotationTimer = setInterval(() => {
        setRotationAngle((prev) => Number(((prev + 0.3) % 360).toFixed(3)));
      }, 50);
    }
    return () => {
      if (rotationTimer) clearInterval(rotationTimer);
    };
  }, [autoRotate]);

  const calculateNodePosition = (index, total) => {
    const angle = ((index / total) * 360 + rotationAngle) % 360;
    const radius = 200;
    const radian = (angle * Math.PI) / 180;
    const x = radius * Math.cos(radian);
    const y = radius * Math.sin(radian);
    const zIndex = Math.round(100 + 50 * Math.cos(radian));
    const opacity = Math.max(0.4, Math.min(1, 0.4 + 0.6 * ((1 + Math.sin(radian)) / 2)));
    return { x, y, zIndex, opacity };
  };

  const isRelatedToActive = (itemId) => {
    if (!activeNodeId) return false;
    return getRelatedItems(activeNodeId).includes(itemId);
  };

  const getStatusStyles = (status) => {
    switch (status) {
      case "completed":
        return "text-white bg-brand-plum border-brand-plum";
      case "in-progress":
        return "text-brand-plum bg-brand-wash border-brand-orchid";
      default:
        return "text-muted bg-line border-line";
    }
  };

  return (
    <div
      className="flex h-[620px] w-full flex-col items-center justify-center overflow-hidden rounded-3xl border border-line bg-brand-bg"
      ref={containerRef}
      onClick={handleContainerClick}
    >
      <div className="relative flex h-full w-full max-w-4xl items-center justify-center">
        <div
          className="absolute flex h-full w-full items-center justify-center"
          ref={orbitRef}
          style={{ perspective: "1000px" }}
        >
          {/* center */}
          <div className="absolute z-10 flex h-16 w-16 items-center justify-center rounded-full bg-cta-gradient">
            <div className="absolute h-20 w-20 animate-ping rounded-full border border-brand-magenta/30 opacity-70" />
            <div
              className="absolute h-24 w-24 animate-ping rounded-full border border-brand-orchid/20 opacity-50"
              style={{ animationDelay: "0.5s" }}
            />
            <div className="h-8 w-8 rounded-full bg-white/90 backdrop-blur-md" />
          </div>

          <div className="absolute h-96 w-96 rounded-full border border-brand-plum/10" />

          {timelineData.map((item, index) => {
            const position = calculateNodePosition(index, timelineData.length);
            const isExpanded = expandedItems[item.id];
            const isRelated = isRelatedToActive(item.id);
            const isPulsing = pulseEffect[item.id];
            const Icon = item.icon;

            const nodeStyle = {
              transform: `translate(${position.x}px, ${position.y}px)`,
              zIndex: isExpanded ? 200 : position.zIndex,
              opacity: isExpanded ? 1 : position.opacity,
            };

            return (
              <div
                key={item.id}
                ref={(el) => {
                  nodeRefs.current[item.id] = el;
                }}
                className="absolute cursor-pointer transition-all duration-700"
                style={nodeStyle}
                onClick={(e) => {
                  e.stopPropagation();
                  toggleItem(item.id);
                }}
              >
                <div
                  className={cn("absolute rounded-full", isPulsing && "animate-pulse duration-1000")}
                  style={{
                    background: "radial-gradient(circle, rgba(184,47,171,0.18) 0%, rgba(184,47,171,0) 70%)",
                    width: `${item.energy * 0.5 + 40}px`,
                    height: `${item.energy * 0.5 + 40}px`,
                    left: `-${(item.energy * 0.5) / 2}px`,
                    top: `-${(item.energy * 0.5) / 2}px`,
                  }}
                />

                <div
                  className={cn(
                    "flex h-12 w-12 items-center justify-center rounded-full border-2 transition-all duration-300",
                    isExpanded
                      ? "scale-150 border-brand-magenta bg-brand-plum text-white shadow-lg shadow-brand-magenta/30"
                      : isRelated
                      ? "animate-pulse border-brand-orchid bg-brand-wash text-brand-plum"
                      : "border-line bg-white text-brand-plum"
                  )}
                >
                  <Icon size={18} />
                </div>

                <div
                  className={cn(
                    "absolute top-14 whitespace-nowrap text-xs font-semibold tracking-wide transition-all duration-300",
                    isExpanded ? "scale-110 text-brand-plum" : "text-brand-plum/70"
                  )}
                >
                  {item.title}
                </div>

                {isExpanded && (
                  <div className="absolute left-1/2 top-24 w-72 -translate-x-1/2 overflow-visible rounded-xl border border-line bg-white p-4 text-left shadow-xl">
                    <div className="absolute -top-3 left-1/2 h-3 w-px -translate-x-1/2 bg-brand-orchid/60" />
                    <div className="flex items-center justify-between">
                      <span
                        className={cn(
                          "rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase",
                          getStatusStyles(item.status)
                        )}
                      >
                        {item.status === "completed"
                          ? "Step"
                          : item.status === "in-progress"
                          ? "Core"
                          : "Outcome"}
                      </span>
                      <span className="font-mono text-xs text-muted">{item.date}</span>
                    </div>
                    <h4 className="mt-2 font-display text-sm font-semibold text-brand-plum">{item.title}</h4>
                    <p className="mt-1 text-xs leading-relaxed text-muted">{item.content}</p>

                    {item.includes && item.includes.length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-1">
                        {item.includes.map((inc) => (
                          <span
                            key={inc}
                            className="rounded-full bg-brand-bg px-2 py-0.5 text-[10px] text-brand-plum"
                          >
                            {inc}
                          </span>
                        ))}
                      </div>
                    )}

                    <div className="mt-3 border-t border-line pt-2">
                      <div className="mb-1 flex items-center justify-between text-xs">
                        <span className="flex items-center text-muted">
                          <Zap size={10} className="mr-1" /> Progress
                        </span>
                        <span className="font-mono text-muted">{item.energy}%</span>
                      </div>
                      <div className="h-1 w-full overflow-hidden rounded-full bg-line">
                        <div className="h-full bg-cta-gradient" style={{ width: `${item.energy}%` }} />
                      </div>
                    </div>

                    {item.relatedIds.length > 0 && (
                      <div className="mt-3 border-t border-line pt-2">
                        <div className="mb-1.5 flex items-center">
                          <LinkIcon size={10} className="mr-1 text-muted" />
                          <h5 className="text-[10px] font-medium uppercase tracking-wider text-muted">
                            Next steps
                          </h5>
                        </div>
                        <div className="flex flex-wrap gap-1">
                          {item.relatedIds.map((relatedId) => {
                            const related = timelineData.find((i) => i.id === relatedId);
                            return (
                              <button
                                key={relatedId}
                                type="button"
                                className="flex h-6 items-center rounded-md border border-line bg-transparent px-2 text-xs text-brand-plum transition-all hover:bg-brand-wash"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleItem(relatedId);
                                }}
                              >
                                {related?.title}
                                <ArrowRight size={8} className="ml-1 text-brand-magenta" />
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
